import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import {
  Camera, SealCheck, Star, WhatsappLogo, ArrowSquareOut,
} from '@phosphor-icons/react';

const ROLE_STYLES = {
  admin: 'bg-danger-soft text-danger border-danger',
  moderator: 'bg-purple-50 text-purple-700 border-purple-200',
  user: 'bg-surface_muted text-text_main border-border',
};

const formatDate = (iso, locale) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(locale || 'fr-FR', {
      year: 'numeric', month: 'long', day: 'numeric',
    });
  } catch { return '—'; }
};

/**
 * Public profile tab: avatar, badges, display name, bio, Facebook URL,
 * WhatsApp toggle, seller type + business fields. Keeps state in parent.
 */
export const PublicTab = ({
  profile,
  fileRef, onPhotoSelect, onOpenFileDialog, getAvatarUrl,
  displayName, setDisplayName,
  bio, setBio, bioMax,
  facebookUrl, setFacebookUrl, isValidFacebookUrl,
  whatsappEnabled, setWhatsappEnabled, hasVerifiedPhone,
  sellerType, setSellerType,
  businessName, setBusinessName, businessNameMissing,
  businessAddress, setBusinessAddress,
  businessNif, setBusinessNif,
  onSave, saving,
  language,
  t,
}) => (
  <>
    {/* Photo + badges header */}
    <div className="bg-white border border-border rounded-2xl p-6" data-testid="photo-section">
      <h2 className="font-semibold font-heading mb-4">{t('settings.profilePhoto')}</h2>
      <div className="flex items-start gap-5">
        <div className="relative">
          <div
            className="w-24 h-24 rounded-full bg-primary flex items-center justify-center text-white text-3xl font-bold overflow-hidden"
            data-testid="avatar-display"
          >
            {profile?.picture ? (
              <img src={getAvatarUrl(profile.picture)} alt={profile.name} className="w-full h-full object-cover" />
            ) : (
              profile?.name?.charAt(0).toUpperCase() || 'U'
            )}
          </div>
          <button
            onClick={onOpenFileDialog}
            className="absolute bottom-0 right-0 bg-primary text-white rounded-full p-1.5 hover:bg-primary-hover"
            data-testid="change-photo-btn"
          >
            <Camera size={16} />
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={onPhotoSelect}
            data-testid="photo-input"
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-2" data-testid="trust-badges">
            {profile?.is_verified && (
              <span
                className="inline-flex items-center gap-1 text-primary text-xs font-medium px-2 py-0.5 rounded-full bg-primary/10 border border-primary/20"
                data-testid="verified-badge"
              >
                <SealCheck size={14} weight="fill" /> {t('settings.verified')}
              </span>
            )}
            {profile?.role && profile.role !== 'user' && (
              <span
                className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full border capitalize ${ROLE_STYLES[profile.role] || ROLE_STYLES.user}`}
                data-testid="profile-role-badge"
              >
                {profile.role}
              </span>
            )}
          </div>
          <p className="text-xs text-text_muted" data-testid="member-since">
            {t('settings.memberSinceLabel')}: <span className="font-medium text-text_main">{formatDate(profile?.created_at, language)}</span>
          </p>
          <p className="text-xs text-text_muted mt-1 flex items-center gap-1" data-testid="avg-rating">
            {t('settings.averageRating')}:{' '}
            {profile?.total_reviews > 0 ? (
              <>
                <Star size={13} weight="fill" className="text-warning" />
                <span className="font-medium text-text_main">{profile.average_rating.toFixed(1)}</span>
                <span>({profile.total_reviews})</span>
              </>
            ) : (
              <span>{t('settings.noReviewsYet')}</span>
            )}
          </p>
          <p className="text-xs text-text_muted mt-2">JPG, PNG, WEBP. {t('settings.photoHelper')}</p>
          <a
            href={profile?.user_id ? `/users/${profile.user_id}` : '#'}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-2"
            data-testid="view-public-profile-btn"
          >
            <ArrowSquareOut size={13} /> {t('settings.viewPublicProfile')}
          </a>
        </div>
      </div>
    </div>

    {/* Display name / bio / Facebook / WhatsApp */}
    <div className="bg-white border border-border rounded-2xl p-6" data-testid="info-section">
      <h2 className="font-semibold font-heading mb-4">
        {t('profileSettings.categories.public')}
      </h2>
      <div className="space-y-4">
        <div>
          <Label htmlFor="display-name-input">{t('settings.displayName')}</Label>
          <Input
            id="display-name-input"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder={t('settings.displayNamePlaceholder')}
            maxLength={40}
            minLength={2}
            className="border border-border"
            data-testid="display-name-input"
          />
          <p className="text-[11px] text-text_muted mt-1">{t('settings.displayNameHint')}</p>
        </div>

        <div>
          <div className="flex items-baseline justify-between">
            <Label>{t('settings.aboutMe')}</Label>
            <span
              className={`text-xs ${bio.length > bioMax ? 'text-danger' : 'text-text_muted'}`}
              data-testid="bio-counter"
            >
              {t('settings.bioCounter', { count: bio.length })}
            </span>
          </div>
          <textarea
            value={bio}
            onChange={(e) => setBio(e.target.value.slice(0, bioMax))}
            placeholder={t('settings.aboutPlaceholder')}
            rows={3}
            maxLength={bioMax}
            className="w-full border border-border rounded-md px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/30"
            data-testid="bio-input"
          />
        </div>

        <div>
          <Label>{t('settings.facebookUrl')}</Label>
          <Input
            value={facebookUrl}
            onChange={(e) => setFacebookUrl(e.target.value)}
            placeholder={t('profileSettings.public.facebookPlaceholder')}
            type="url"
            className="border border-border"
            data-testid="facebook-url-input"
          />
          {facebookUrl && !isValidFacebookUrl(facebookUrl) && (
            <p className="text-xs text-danger mt-1" data-testid="facebook-invalid-hint">
              {t('profileSettings.public.facebookInvalid')}
            </p>
          )}
        </div>

        <div className="flex items-center justify-between border border-border rounded-lg p-3">
          <div className="flex items-center gap-2">
            <WhatsappLogo size={20} className="text-success" weight="fill" />
            <div>
              <p className="text-sm font-medium" data-testid="whatsapp-toggle-label">
                {t('settings.whatsappAvailable')}
              </p>
              {!hasVerifiedPhone && (
                <p className="text-xs text-text_muted mt-0.5" data-testid="whatsapp-requires-phone">
                  {t('settings.whatsappRequiresPhone')}
                </p>
              )}
            </div>
          </div>
          <Switch
            checked={whatsappEnabled}
            onCheckedChange={(v) => setWhatsappEnabled(v)}
            disabled={!hasVerifiedPhone}
            data-testid="whatsapp-toggle"
          />
        </div>

        <Button
          onClick={onSave}
          disabled={saving || bio.length > bioMax}
          variant="primary"
          className="font-semibold px-8"
          data-testid="save-profile-btn"
        >
          {saving ? t('common.loading') : t('settings.saveChanges')}
        </Button>
      </div>
    </div>

    {/* Seller Type */}
    <div className="bg-white border border-border rounded-2xl p-6" data-testid="seller-type-section">
      <h2 className="font-semibold font-heading mb-4">{t('settings.sellerTypeTitle')}</h2>
      <div
        className="grid grid-cols-2 gap-3 mb-4"
        role="radiogroup"
        aria-label={t('settings.sellerTypeTitle')}
      >
        {[
          { key: 'private', label: t('sellerType.private'), desc: t('settings.sellerTypePrivateDesc') },
          { key: 'business', label: t('sellerType.business'), desc: t('settings.sellerTypeBusinessDesc') },
        ].map((opt) => {
          const active = sellerType === opt.key;
          return (
            <button
              key={opt.key}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => setSellerType(opt.key)}
              className={`text-left rounded-xl border px-4 py-3 transition-colors ${
                active
                  ? 'border-primary bg-primary/5 ring-1 ring-primary/20'
                  : 'border-border hover:border-primary/40'
              }`}
              data-testid={`seller-type-${opt.key}`}
            >
              <p className={`text-sm font-semibold ${active ? 'text-primary' : 'text-text_main'}`}>
                {opt.label}
              </p>
              <p className="text-xs text-text_muted mt-0.5">{opt.desc}</p>
            </button>
          );
        })}
      </div>

      {sellerType === 'business' && (
        <div className="space-y-4 border-t border-border pt-4" data-testid="business-fields">
          <div>
            <Label>
              {t('settings.businessName')} <span className="text-danger">*</span>
            </Label>
            <Input
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
              placeholder={t('settings.businessNamePlaceholder')}
              className={`border ${businessNameMissing ? 'border-danger ring-1 ring-danger' : 'border-border'}`}
              data-testid="business-name-input"
              aria-invalid={businessNameMissing || undefined}
              aria-describedby={businessNameMissing ? 'business-name-error' : undefined}
            />
            {businessNameMissing && (
              <p
                id="business-name-error"
                className="text-xs text-danger mt-1"
                data-testid="business-name-error"
              >
                {t('settings.businessNameRequired')}
              </p>
            )}
          </div>
          <div>
            <Label>
              {t('settings.businessAddress')} <span className="text-text_muted font-normal">({t('common.optional')})</span>
            </Label>
            <Input
              value={businessAddress}
              onChange={(e) => setBusinessAddress(e.target.value)}
              placeholder={t('settings.businessAddressPlaceholder')}
              className="border border-border"
              data-testid="business-address-input"
            />
          </div>
          <div>
            <Label>
              {t('settings.businessNif')} <span className="text-text_muted font-normal">({t('common.optional')})</span>
            </Label>
            <Input
              value={businessNif}
              onChange={(e) => setBusinessNif(e.target.value)}
              placeholder="—"
              className="border border-border"
              data-testid="business-nif-input"
            />
          </div>
          <p className="text-xs text-text_muted">{t('settings.sellerTypeOptionalHint')}</p>
        </div>
      )}

      <Button
        onClick={onSave}
        disabled={saving || businessNameMissing}
        variant="primary"
        className="font-semibold px-8 mt-4 disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="save-seller-type-btn"
      >
        {saving ? t('common.loading') : t('settings.saveChanges')}
      </Button>
    </div>
  </>
);
